import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { usePublicJson } from '../utils/use-public-json';
import { RatingChip, type RatingTag } from '../leagues/rating-chip';
import { ratingForTag, sortByRating } from './participants-order';

interface Entrant {
  id: number;
  displayName: string;
  singlesRating: number | null;
  doublesRating: number | null;
}

interface Registration {
  id: number;
  status: string;
  player: Entrant;
  partner: Entrant | null;
}

interface CategoryWithParticipants {
  id: number;
  name: string;
  discipline: string | null;
  maxPlayers: number | null;
  registrationCount: number;
  registrations: Registration[];
}

interface ParticipantsData {
  categories: CategoryWithParticipants[];
}

const DOUBLES_DISCIPLINES = ['DOUBLES', 'DOUBLES_M', 'DOUBLES_F', 'DOUBLES_W', 'DOUBLES_MIX', 'MIXED'];

function ratingTagOf(discipline: string | null): RatingTag {
  if (discipline != null && DOUBLES_DISCIPLINES.includes(discipline)) {
    return 'D';
  }

  return 'S';
}

function monogram(displayName: string): string {
  const [first = '', last = ''] = displayName.trim().split(/\s+/);

  return ((first.charAt(0) || '?') + (last.charAt(0) || '')).toUpperCase();
}

function EntrantLine(props: { entrant: Entrant; tag: RatingTag }) {
  const { entrant, tag } = props;

  return (
    <span className="participant-entrant">
      <span className="participant-entrant__monogram" aria-hidden="true">
        {monogram(entrant.displayName)}
      </span>
      <span className="participant-entrant__name">{entrant.displayName}</span>
      <span className="participant-entrant__rating">
        <RatingChip tag={tag} value={ratingForTag(entrant, tag)} />
      </span>
    </span>
  );
}

function RegistrationGroup(props: { title: string; hint?: string; entries: Registration[]; tag: RatingTag }) {
  const { title, hint, entries, tag } = props;

  if (entries.length === 0) {
    return null;
  }

  return (
    <div className="participants-group">
      <h3 className="participants-group-title">
        {title}
        <span className="participants-group-count">{entries.length}</span>
      </h3>
      {hint && <p className="participants-group-hint">{hint}</p>}
      <ol className="participants-list">
        {entries.map((registration) => (
          <li key={registration.id} className="participants-item">
            <EntrantLine entrant={registration.player} tag={tag} />
            {registration.partner && <EntrantLine entrant={registration.partner} tag={tag} />}
          </li>
        ))}
      </ol>
    </div>
  );
}

const Participants: React.FC = () => {
  const { id: tournamentId } = useParams<{ id: string }>();
  const { data, loading, error } = usePublicJson<ParticipantsData>(
    tournamentId ? `/api/public/events/${tournamentId}/participants` : null,
  );

  if (loading) {
    return <div className="participants-loading">Ładowanie...</div>;
  }

  if (error || !data) {
    return <div className="participants-error">Nie udało się załadować listy uczestników.</div>;
  }

  return (
    <div className="participants">
      <Link to={`/events/${tournamentId}`} className="participants-back">
        ← Powrót do turnieju
      </Link>
      <h1>Uczestnicy</h1>
      {data.categories.map((cat) => {
        const tag = ratingTagOf(cat.discipline);

        return (
          <section key={cat.id} className="participants-category">
            <h2 className="participants-category-name">
              {cat.name}
              <span className="participants-category-count">
                {cat.maxPlayers === null
                  ? `${cat.registrationCount}`
                  : `${cat.registrationCount} / ${cat.maxPlayers}`}
              </span>
            </h2>
            {cat.registrations.length === 0 ? (
              <p className="participants-empty">Brak zapisanych.</p>
            ) : (
              <>
                <RegistrationGroup
                  title="Lista startowa"
                  tag={tag}
                  entries={sortByRating(cat.registrations.filter((reg) => reg.status === 'CONFIRMED'), tag)}
                />
                <RegistrationGroup
                  title="Czekają na potwierdzenie"
                  hint="Organizator musi zaakceptować te zgłoszenia — dopiero wtedy wejdą na listę startową."
                  tag={tag}
                  entries={sortByRating(cat.registrations.filter((reg) => reg.status === 'PENDING'), tag)}
                />
                <RegistrationGroup
                  title="Lista oczekujących"
                  hint="Zgłoszenia po terminie. Wchodzą do gry, gdy zwolni się miejsce."
                  tag={tag}
                  entries={cat.registrations.filter((reg) => reg.status === 'WAITLIST')}
                />
              </>
            )}
          </section>
        );
      })}
    </div>
  );
};

export default Participants;
